import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import * as MuiIcons from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import api, { API_BASE_URL } from "../../utils/api";
import AddWorkOrderSmart from "./addWorkorder";
import MemberWorkordersDetails from "./MemberWorkordersDetails";

const ITEMS_PER_PAGE = 5;

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const nestedCardSx = {
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(255,255,255,0.98)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const FilterToolbar = ({ search, onSearch, sortOrder, onSort, statuses, statusFilter, onStatusFilter, label = "work orders" }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.25 }}>
      <TextField
        size="small"
        placeholder={`Search ${label}…`}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: "1 1 200px",
          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
        }}
      />

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortOrder}
          label="Sort By"
          onChange={(e) => onSort(e.target.value)}
          sx={{ borderRadius: "10px" }}
        >
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="oldest">Oldest First</MenuItem>
        </Select>
      </FormControl>
    </Box>

    {statuses.length > 0 && (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip
          label="All"
          size="small"
          onClick={() => onStatusFilter(null)}
          sx={{
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: statusFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
            color: statusFilter === null ? "#2f63ad" : "#5f6b7a",
            border: statusFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
          }}
        />
        {statuses.map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            onClick={() => onStatusFilter(s === statusFilter ? null : s)}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: statusFilter === s ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
              color: statusFilter === s ? "#2f63ad" : "#5f6b7a",
              border: statusFilter === s ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

// ─── Work order card ─────────────────────────────────────────────────────────

const WorkOrderCard = ({ order, onView, statuses }) => {
  const theme = useTheme();
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/workorder-files/${order.order_id}`)
      .then((res) => {
        if (cancelled) return;
        const img = (res.data || []).find((f) => f.file_type?.startsWith("image/"));
        if (img) {
          const raw = img.file_url || "";
          const url = raw.startsWith("http")
            ? raw
            : `${API_BASE_URL}/workorderimages/${encodeURIComponent(raw.split("/").pop())}`;
          setThumbUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [order.order_id]);

  const getStatusInfo = useCallback(
    (statusName) => statuses.find((s) => s.statusname === statusName),
    [statuses]
  );

  const renderStatusChip = (o) => {
    const statusInfo = getStatusInfo(o.status);
    const normalized = String(o?.status || "").toLowerCase();
    let chipStyles = { color: "#2f63ad", backgroundColor: "rgba(47,99,173,0.10)", fontWeight: 700 };
    if (["resolved", "closed", "completed"].includes(normalized) || o?.resolved_at) {
      chipStyles = { color: "#15803d", backgroundColor: "rgba(34,197,94,0.12)", fontWeight: 700 };
    } else if (normalized) {
      chipStyles = { color: "#b45309", backgroundColor: "rgba(245,158,11,0.12)", fontWeight: 700 };
    }
    return (
      <Chip size="small" label={statusInfo?.statusname || o.status || "Unknown"} sx={chipStyles} />
    );
  };

  const renderStatusLine = (o) => {
    const statusInfo = getStatusInfo(o.status);
    const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];
    const themeColor =
      statusInfo?.color && theme.palette[statusInfo.color]
        ? theme.palette[statusInfo.color].main
        : statusInfo?.color || "#5f6b7a";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {IconComponent && <IconComponent fontSize="small" sx={{ color: themeColor }} />}
        <Typography sx={{ color: themeColor, fontWeight: 700, fontSize: "0.875rem" }}>
          {statusInfo?.statusname || o.status || "Unknown"}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...nestedCardSx,
        p: 0,
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      {/* Image thumbnail (left) */}
      {thumbUrl && (
        <Box
          component="img"
          src={thumbUrl}
          alt="Work Order"
          sx={{
            width: 110,
            minWidth: 110,
            objectFit: "cover",
            borderRadius: "14px 0 0 14px",
            flexShrink: 0,
            display: "block",
          }}
        />
      )}

      {/* Card content (right) */}
      <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
              Work Order #{order.order_id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 600, mb: 0.5 }}>
              {order.type_name || "N/A"}{order.subtype_name ? ` • ${order.subtype_name}` : ""}
            </Typography>
          </Box>
          {renderStatusChip(order)}
        </Box>

        <Box sx={{ mb: 1.5 }}>{renderStatusLine(order)}</Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2, flexGrow: 1 }}>
          {order.description || "No description available."}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onView}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            View Details
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const WorkOrders = ({ workOrders = [], propertyId }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileDetail, setMobileDetail] = useState({ open: false, id: null });

  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get(`/statuses`);
        setStatuses(response.data || []);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  const isResolvedStatus = (order) => {
    const normalized = String(order?.status || "").toLowerCase();
    return !!order?.resolved_at || ["resolved", "closed", "completed"].includes(normalized);
  };

  const resolvedCount = useMemo(
    () => workOrders.filter((order) => isResolvedStatus(order)).length,
    [workOrders]
  );

  const openCount = workOrders.length - resolvedCount;

  // Unique statuses from data for filter chips
  const uniqueStatuses = useMemo(
    () => [...new Set(workOrders.map((w) => w.status).filter(Boolean))].sort(),
    [workOrders]
  );

  // Apply search + sort + status filter
  const filteredWorkOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = workOrders;

    if (q) {
      result = result.filter(
        (w) =>
          String(w.order_id || "").includes(q) ||
          String(w.type_name || "").toLowerCase().includes(q) ||
          String(w.subtype_name || "").toLowerCase().includes(q) ||
          String(w.description || "").toLowerCase().includes(q) ||
          String(w.status || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      result = result.filter((w) => w.status === statusFilter);
    }

    return [...result].sort((a, b) => {
      const da = new Date(a.created_at || a.updated_at || 0);
      const db = new Date(b.created_at || b.updated_at || 0);
      return sortOrder === "newest" ? db - da : da - db;
    });
  }, [workOrders, search, sortOrder, statusFilter]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };
  const handleSort = (val) => { setSortOrder(val); setPage(1); };
  const handleStatusFilter = (val) => { setStatusFilter(val); setPage(1); };
  const handleOpen = () => setOpen(true);
  const handleClose = () => { setOpen(false); setPage(1); };

  const paginatedWorkOrders = filteredWorkOrders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const isFiltering = search.trim() !== "" || statusFilter !== null;
  const previewItems = isFiltering ? filteredWorkOrders.slice(0, 4) : filteredWorkOrders.slice(0, 2);


  return (
    <>
      <AddWorkOrderSmart
        open={openAddModal}
        handleClose={() => setOpenAddModal(false)}
        propertyId={propertyId}
        onWorkOrderAdded={() => {
          setShowSuccessSnackbar(true);
          window.location.reload();
        }}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setShowSuccessSnackbar(false)} sx={{ width: "100%" }}>
          Work order created successfully!
        </Alert>
      </Snackbar>

      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Work Orders
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review submitted work orders, status updates, and recent activity.
          </Typography>
        </Box>

        {/* Summary stat cards */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Total Work Orders</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{workOrders.length}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(47,99,173,0.10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <HomeRepairServiceOutlinedIcon sx={{ color: "#2f63ad" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Open</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{openCount}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <PendingActionsOutlinedIcon sx={{ color: "#d97706" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Resolved</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{resolvedCount}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(34,197,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Preview section */}
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
            Recent Work Orders
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.75 }}>
            A quick preview of the latest work orders for this account.
          </Typography>

          <FilterToolbar
            search={search}
            onSearch={handleSearch}
            sortOrder={sortOrder}
            onSort={handleSort}
            statuses={uniqueStatuses}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
          />
        </Box>

        {workOrders.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
              No work orders found for this account.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setOpenAddModal(true)}
              sx={{ backgroundColor: "#2f63ad", borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { backgroundColor: "#1f4f94" } }}
            >
              Create New Work Order
            </Button>
          </Paper>
        ) : filteredWorkOrders.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              No work orders match your search or filter.
            </Typography>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              {previewItems.map((order) => (
                <Grid item xs={12} md={6} key={order.order_id}>
                  <WorkOrderCard
                    order={order}
                    onView={() => isMobile
                      ? setMobileDetail({ open: true, id: order.order_id })
                      : navigate(`/home/memberspage/workorder/${order.order_id}`)
                    }
                    statuses={statuses}
                  />
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                pt: 2,
                pb: 1,
                display: "flex",
                justifyContent: { xs: "stretch", sm: "space-between" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenAddModal(true)}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                Create New Work Order
              </Button>

              {filteredWorkOrders.length > previewItems.length && (
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "0 8px 20px rgba(47,99,173,0.22)" },
                  }}
                >
                  View All{filteredWorkOrders.length !== workOrders.length
                    ? ` ${filteredWorkOrders.length} Matching`
                    : ""} Work Orders
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* View All Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 18px 60px rgba(0,0,0,0.16)" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          All Work Orders
          {filteredWorkOrders.length !== workOrders.length && (
            <Typography component="span" variant="body2" sx={{ color: "#6b778c", ml: 1, fontWeight: 400 }}>
              ({filteredWorkOrders.length} of {workOrders.length})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <FilterToolbar
            search={search}
            onSearch={handleSearch}
            sortOrder={sortOrder}
            onSort={handleSort}
            statuses={uniqueStatuses}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
          />

          {paginatedWorkOrders.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 3 }}>
              No work orders match your search or filter.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {paginatedWorkOrders.map((order) => (
                <Grid item xs={12} key={order.order_id}>
                  <WorkOrderCard
                    order={order}
                    onView={() => {
                      handleClose();
                      isMobile
                        ? setMobileDetail({ open: true, id: order.order_id })
                        : navigate(`/home/memberspage/workorder/${order.order_id}`);
                    }}
                    statuses={statuses}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 1, p: 2 }}>
          <Pagination
            count={Math.ceil(filteredWorkOrders.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
          />
          <Button onClick={handleClose} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile detail dialog */}
      <Dialog
        open={mobileDetail.open}
        onClose={() => setMobileDetail({ open: false, id: null })}
        fullScreen
        PaperProps={{ sx: { m: 0, borderRadius: 0 } }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            backgroundColor: "rgba(255,255,255,0.95)",
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton onClick={() => setMobileDetail({ open: false, id: null })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>
          {mobileDetail.id && (
            <MemberWorkordersDetails
              id={String(mobileDetail.id)}
              onClose={() => setMobileDetail({ open: false, id: null })}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default WorkOrders;
